import React from 'react';
import Box from '@amzn/meridian/box';
import Text from '@amzn/meridian/text';
import { getBadgePhotoUrl, getInitials } from '../utils';

export interface AssociateAvatarProps {
  /** The associate's display name, used for initials fallback and img alt. */
  associateName?: string;
  /** The associate's login alias, used to build the badge photo URL. */
  associateAlias?: string;
  /**
   * Class name applied to the circular avatar container. Callers pass their
   * page-specific class (e.g. `associate-view-row__avatar`) so the avatar
   * matches the surrounding layout.
   */
  className?: string;
}

/**
 * Circular associate avatar shared across the Station Assignment workflow.
 *
 * Renders the associate's badge photo (built from `associateAlias`) when an
 * alias is available; otherwise, or if the photo fails to load, falls back to
 * 1–2 character initials derived from `associateName`.
 */
const AssociateAvatar: React.FC<AssociateAvatarProps> = ({
  associateName,
  associateAlias,
  className,
}) => {
  const photoUrl = getBadgePhotoUrl(associateAlias);
  const initials = getInitials(associateName ?? '');

  // Track which alias's photo failed to load, rather than a plain boolean, so
  // the failure flag resets synchronously when the alias changes. This avoids
  // a stale `photoFailed` incorrectly suppressing a different associate's photo
  // when the same component instance is reused (e.g. SearchAssociateModal
  // swaps `selectedAssociate` without remounting) — with no extra render or
  // flash of initials that a reset-in-effect would cause.
  const [failedAlias, setFailedAlias] = React.useState<string | undefined>(undefined);
  const photoFailed = failedAlias === associateAlias;

  const showPhoto = Boolean(photoUrl) && !photoFailed;

  return (
    <Box className={className}>
      {showPhoto ? (
        <img
          className="associate-avatar__img"
          src={photoUrl}
          alt={associateName || 'Associate'}
          onError={() => setFailedAlias(associateAlias)}
        />
      ) : (
        <Text type="b200">{initials}</Text>
      )}
    </Box>
  );
};

export default AssociateAvatar;
